import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { FirebaseUtilitiesService } from '../../services/firebase-utilities.service';
import { UIMessagingService } from '../../services/uimessaging.service';

/** Validates email domain format: letters/numbers/hyphens + dot + TLD. Empty is allowed. */
function optionalEmailDomainValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value?.trim();
    if (!v) return null;
    return /^[a-zA-Z0-9][a-zA-Z0-9.-]*\.[a-zA-Z]{2,}$/.test(v) ? null : { invalidDomain: true };
  };
}

export interface FacilitySetupData {
  facilityName?: string;
  facilityId?: string | null;
  userData?: any;
}

/** Validates URL only when a value is entered; empty is allowed. */
function optionalUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value?.trim();
    if (!v) return null;
    try {
      new URL(v);
      return null;
    } catch {
      return { invalidUrl: true };
    }
  };
}

@Component({
  selector: 'app-facility-setup',
  templateUrl: './facility-setup.component.html',
  styleUrls: ['./facility-setup.component.scss'],
  standalone: false
})
export class FacilitySetupComponent implements OnInit {
  facilityName: string = '';
  facilityId: string | null = null;
  isHubUser: boolean;
  pacsUrlControl = new UntypedFormControl('', [optionalUrlValidator()]);
  emailDomainControl = new UntypedFormControl('', [optionalEmailDomainValidator()]);
  timezoneControl = new UntypedFormControl('America/New_York', [Validators.required]);
  loading = false;
  saving = false;

  readonly timezoneOptions = [
    { label: 'Eastern Time (ET)', value: 'America/New_York' },
    { label: 'Central Time (CT)', value: 'America/Chicago' },
    { label: 'Mountain Time (MT)', value: 'America/Denver' },
    { label: 'Mountain Time – Arizona (no DST)', value: 'America/Phoenix' },
    { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
    { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
    { label: 'Hawaii Time (HST)', value: 'Pacific/Honolulu' },
    { label: 'Atlantic Time (AT)', value: 'America/Halifax' },
    { label: 'UTC', value: 'UTC' },
  ];

  constructor(
    public dialogRef: MatDialogRef<FacilitySetupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FacilitySetupData,
    private auth_$: AuthService,
    private firebaseUtilities_$: FirebaseUtilitiesService,
    private uiMessaging_$: UIMessagingService,
    private router: Router,
  ) {
    this.facilityName = data?.facilityName || '';
    this.facilityId = data?.facilityId ?? null;
    this.isHubUser = !data?.userData?.facility;
  }

  navigateToUsage(): void {
    this.dialogRef.close();
    this.router.navigate(['/hub/usage']);
  }

  get quotaOwnerID(): string | null {
    const user = this.auth_$.userData.value as any;
    return user?.ownerID && user?.plancode !== 'spoke' ? user.ownerID : null;
  }

  ngOnInit(): void {
    if (this.facilityId) {
      this.loadPacsUrl();
    }
  }

  private async loadPacsUrl(): Promise<void> {
    if (!this.facilityId) return;
    this.loading = true;
    try {
      const [url, domain, timezone] = await Promise.all([
        this.auth_$.getFacilityPacsURL(this.facilityId),
        this.firebaseUtilities_$.getFacilityEmailDomain(this.facilityId),
        this.firebaseUtilities_$.getFacilityTimezone(this.facilityId)
      ]);
      this.pacsUrlControl.setValue(url ?? '');
      this.emailDomainControl.setValue(domain ?? '');
      this.timezoneControl.setValue(timezone);
    } catch (err) {
      console.error('Failed to load facility setup:', err);
      this.uiMessaging_$.toastMessage('Failed to load facility settings.', 'OK');
    } finally {
      this.loading = false;
    }
  }

  async onSave(): Promise<void> {
    if (!this.facilityId) {
      this.uiMessaging_$.toastMessage('No facility selected.', 'OK');
      return;
    }
    const url = this.pacsUrlControl.value?.trim() ?? '';
    const domain = this.emailDomainControl.value?.trim() ?? '';
    const timezone = this.timezoneControl.value?.trim() ?? 'America/New_York';
    if (this.pacsUrlControl.invalid && this.pacsUrlControl.errors?.['invalidUrl']) {
      this.pacsUrlControl.markAsTouched();
      this.uiMessaging_$.toastMessage('Please enter a well-formatted URL (e.g. https://example.com).', 'OK');
      return;
    }
    if (this.emailDomainControl.invalid && this.emailDomainControl.errors?.['invalidDomain']) {
      this.emailDomainControl.markAsTouched();
      this.uiMessaging_$.toastMessage('Please enter a valid email domain (e.g. example.com).', 'OK');
      return;
    }
    this.saving = true;
    try {
      await this.firebaseUtilities_$.updateFacilitySetup(this.facilityId, url, domain, timezone);

      // Keep the in-memory user object in sync so facilityPACSUrl reflects the new value immediately
      const current = this.auth_$.userData.value as any;
      this.auth_$.userData.next({ ...current, facilityPACSUrl: url || null });

      this.uiMessaging_$.toastMessage('Facility settings saved.', 'OK');
      this.dialogRef.close(true);
    } catch (err) {
      console.error('Failed to save PACS URL:', err);
      this.uiMessaging_$.toastMessage('Failed to save PACS URL. Please try again.', 'OK');
    } finally {
      this.saving = false;
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }
}
